import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { apiPaths } from "../../../service/apiPaths";
import ApiService from "../../../service/ApiService";
import {
  formatDateToISODateString,
  limit,
  updateComponent,
  ViewType,
} from "../../../util/constant";
import { IEmployee } from "./Employee.props";

const useEmployee = () => {
  const [selectedEmployeeView, setSelectedEmployeeView] = useState<
    string | null
  >(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );
  const [employeeData, setEmployeeData] = useState<IEmployee[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateFilter, setDateFilter] = useState<{fromDate: Date | null, toDate: Date | null}>({
    fromDate: null,
    toDate: null
  });

  const navigate = useNavigate();

  // Fetch all employees (including inactive)
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = "";

      if (dateFilter.fromDate && dateFilter.toDate) {
        const formattedFromDate = formatDateToISODateString(dateFilter.fromDate);
        const formattedToDate = formatDateToISODateString(dateFilter.toDate);

        endpoint = `${apiPaths.getEmployeesWithStatus}?fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${page}&limit=${limit}`;
      } else {
        // Default: fetch all employees without status filter
        endpoint = `${apiPaths.getEmployeesWithStatus}?page=${page}&limit=${limit}`;
      }

      if (search) {
        endpoint += `&search=${search}`;
      }

      console.log("Fetching employees from:", endpoint);

      const result = await ApiService({
        method: "GET",
        endpoint,
      });

      console.log("API Response:", result);

      setEmployeeData(result.data);
      setTotalPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFilter.fromDate, dateFilter.toDate]);

  // Initial load and when dependencies change
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleDateFilter = async (fromDate: Date | null, toDate: Date | null) => {
    setPage(1); // Reset to first page when filtering
    setDateFilter({ fromDate, toDate });
  };

  const handleConfirmStatusUp = async () => {
    setLoading(true);
    const status = employeeData.find(
      (employee: any) => employee.id === deletingId
    );
    const data = {
      status: status?.status.toString() === "active" ? "inactive" : "active",
    };
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateEmployee}${deletingId}/status`,
        data: data,
      });
      
      // Refetch data to get updated list
      await fetchEmployees();
    } catch (error) {
      console.error("Error updating employee status:", error);
    } finally {
      setLoading(false);
    }
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleView = (id: string) => {
    setSelectedEmployeeView(id);
  };

  const handleEdit = (id: string) => {
    setSelectedEmployeeId(id);
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };

  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addEmployee");
  };

  const updateScreen = (type: ViewType) => {
    updateComponent(type, setSelectedEmployeeView, setSelectedEmployeeId);
  };

  return {
    selectedEmployeeView,
    selectedEmployeeId,
    employeeData,
    showConfirmation,
    handleView,
    handleAdd,
    handleEdit,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    updateScreen,
    loading,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
  };
};

export default useEmployee;